import { useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { JobRun } from "~/components/job-log";
import {
  SubmitCategorySchemaMigrationDocument,
  type SubmitCategorySchemaMigrationMutation,
  type SubmitCategorySchemaMigrationMutationVariables,
} from "~/features/admin/categories/gqls";
import type { CategorySchemaMigrationRunAction } from "~/features/admin/categories/types";
import {
  type CategorySchemaMigrationPipelineJobHistoryItem,
  DataImportPipelineJobHistoryService,
  type DipPipelineDryRunResultTarget,
  toPipelineDryRunResultTarget,
  useListDataImportPipelineJobDryRunHistory,
} from "~/features/dataImportProjects";
import { awaitRunOutcomeFromLogs } from "~/features/dataImportProjects/utils/awaitRunOutcomeFromLogs";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseCategorySchemaMigrationRunProps {
  onRefresh: () => Promise<unknown>;
}

interface UseCategorySchemaMigrationRunReturn {
  runningPlanKey: string | null;
  runningAction: CategorySchemaMigrationRunAction | null;
  jobRun: JobRun | null;
  dryRunResult: DipPipelineDryRunResultTarget | null;
  dryRunResults: ReadonlyMap<string, DipPipelineDryRunResultTarget>;
  clearJobRun: () => void;
  clearDryRunResult: () => void;
  openDryRunResult: (result: DipPipelineDryRunResultTarget) => void;
  run: (
    action: CategorySchemaMigrationRunAction,
    planKey: string,
    options?: { autoApprove?: boolean },
  ) => Promise<boolean>;
}

const LOG_OBSERVATION_TIMEOUT_MS = 10 * 60 * 1000;
const MAX_LOG_OBSERVATION_ATTEMPTS = 3;

export function useCategorySchemaMigrationRun({
  onRefresh,
}: UseCategorySchemaMigrationRunProps): UseCategorySchemaMigrationRunReturn {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const historyService = useMemo(() => new DataImportPipelineJobHistoryService(), []);
  const { history } = useListDataImportPipelineJobDryRunHistory();
  const [submitMutation] = useMutation<
    SubmitCategorySchemaMigrationMutation,
    SubmitCategorySchemaMigrationMutationVariables
  >(SubmitCategorySchemaMigrationDocument);
  const runningRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [runningPlanKey, setRunningPlanKey] = useState<string | null>(null);
  const [runningAction, setRunningAction] = useState<CategorySchemaMigrationRunAction | null>(null);
  const [jobRun, setJobRun] = useState<JobRun | null>(null);
  const [dryRunResult, setDryRunResult] = useState<DipPipelineDryRunResultTarget | null>(null);
  const [sessionDryRunResults, setSessionDryRunResults] = useState<ReadonlyMap<string, DipPipelineDryRunResultTarget>>(
    new Map(),
  );
  const dryRunResults = useMemo(() => {
    const results = new Map<string, DipPipelineDryRunResultTarget>();
    for (const item of history ?? []) {
      const planKey = "categorySchemaMigrationPlanKey" in item ? item.categorySchemaMigrationPlanKey : null;
      if (!planKey || item.action !== "dryrun" || item.status !== "completed" || results.has(planKey)) continue;
      const result = toPipelineDryRunResultTarget({
        action: item.action,
        id: item.jobRunId,
        status: item.status,
        batchEvidencesJobRuns: item.response.batchEvidencesJobs,
        batchChangeRequestsJobRuns: item.response.batchChangeRequestsJobs,
      });
      if (result) results.set(planKey, result);
    }
    for (const [planKey, result] of sessionDryRunResults) {
      results.set(planKey, result);
    }
    return results;
  }, [history, sessionDryRunResults]);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    [],
  );

  const refreshAfterRealRun = useCallback(async () => {
    try {
      await onRefresh();
    } catch {
      // The run outcome remains authoritative; a later query or page reload can refresh the list.
    }
  }, [onRefresh]);

  const run = useCallback(
    async (action: CategorySchemaMigrationRunAction, planKey: string, options?: { autoApprove?: boolean }) => {
      if (!tenantId || runningRef.current) {
        return false;
      }

      const dryRun = action === "dryrun";
      const autoApprove = !dryRun && options?.autoApprove === true;
      runningRef.current = true;
      setRunningPlanKey(planKey);
      setRunningAction(action);
      setJobRun(null);
      setDryRunResult(null);
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      try {
        const { data } = await submitMutation({
          variables: {
            tenantId,
            planKey,
            dryRun,
            autoApprove,
          },
        });
        if (abortController.signal.aborted) return false;

        const result = data?.cnutSubmitCategorySchemaMigration;
        if (result?.__typename === "ClientError") {
          throw Object.assign(new Error(result.message), { code: result.code });
        }
        if (result?.__typename !== "CategorySchemaMigrationSubmitOutput") {
          throw new Error(t("feature.categories.recordMigration.runFailed"));
        }
        const batchEvidencesJobs = result.batchEvidencesJobs.map(({ jobId, jobRunId }) => ({ jobId, jobRunId }));
        const batchChangeRequestsJobs = result.batchChangeRequestsJobs.map(({ jobId, jobRunId }) => ({
          jobId,
          jobRunId,
        }));

        setJobRun({
          jobRunId: result.jobRunId,
          jobName: "DataImportPipelineJob",
          dryRun,
        });
        toast.success(
          t(
            dryRun
              ? "feature.categories.recordMigration.dryRunStarted"
              : "feature.categories.recordMigration.runStarted",
          ),
        );

        let outcome: Awaited<ReturnType<typeof awaitRunOutcomeFromLogs>> = "timeout";
        for (let attempt = 0; attempt < MAX_LOG_OBSERVATION_ATTEMPTS; attempt += 1) {
          outcome = await awaitRunOutcomeFromLogs(result.jobRunId, {
            timeoutMs: LOG_OBSERVATION_TIMEOUT_MS,
            signal: abortController.signal,
          });
          if (outcome !== "timeout" || abortController.signal.aborted) break;
        }
        if (abortController.signal.aborted) return false;

        if (outcome === "timeout") {
          if (!dryRun) {
            await refreshAfterRealRun();
          }
          toast.error(t("feature.categories.recordMigration.runTimeout"));
          return false;
        }

        if (outcome === "succeeded") {
          if (dryRun) {
            const completedDryRunResult = toPipelineDryRunResultTarget({
              action,
              id: result.jobRunId,
              status: "completed",
              batchEvidencesJobRuns: batchEvidencesJobs,
              batchChangeRequestsJobRuns: batchChangeRequestsJobs,
            });
            setDryRunResult(completedDryRunResult);
            if (completedDryRunResult) {
              setSessionDryRunResults((current) => new Map(current).set(planKey, completedDryRunResult));
            }
            const historyItem: CategorySchemaMigrationPipelineJobHistoryItem = {
              categorySchemaMigrationPlanKey: planKey,
              jobType: "DataImportPipelineJob",
              action,
              jobId: batchEvidencesJobs[0]?.jobId ?? batchChangeRequestsJobs[0]?.jobId ?? planKey,
              jobRunId: result.jobRunId,
              executedAt: new Date().toISOString(),
              status: "completed",
              parameters: {
                batchEvidencesJobIds: batchEvidencesJobs.map(({ jobId }) => jobId),
                batchChangeRequestsJobIds: batchChangeRequestsJobs.map(({ jobId }) => jobId),
                dryRun: true,
                dryRunOptions: null,
                autoApprove: false,
              },
              response: {
                jobRunId: result.jobRunId,
                batchEvidencesJobs,
                batchChangeRequestsJobs,
              },
            };
            try {
              await historyService.saveHistory("DataImportPipelineJob", historyItem);
            } catch {
              // The completed result remains available in this session even if browser storage is unavailable.
            }
            setJobRun(null);
            toast.success(t("feature.categories.recordMigration.dryRunCompleted"));
          } else {
            await refreshAfterRealRun();
            toast.success(t("feature.categories.recordMigration.runCompleted"));
          }
          return true;
        }

        if (!dryRun && outcome === "failed") {
          await refreshAfterRealRun();
        }
        toast.error(t("feature.categories.recordMigration.runFailed"));
        return false;
      } catch (error) {
        if (abortController.signal.aborted) return false;
        toast.error(translateError(t, error, t("feature.categories.recordMigration.runFailed")));
        return false;
      } finally {
        runningRef.current = false;
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
        if (!abortController.signal.aborted) {
          setRunningPlanKey(null);
          setRunningAction(null);
        }
      }
    },
    [historyService, refreshAfterRealRun, submitMutation, t, tenantId],
  );

  return {
    runningPlanKey,
    runningAction,
    jobRun,
    dryRunResult,
    dryRunResults,
    clearJobRun: () => setJobRun(null),
    clearDryRunResult: () => setDryRunResult(null),
    openDryRunResult: setDryRunResult,
    run,
  };
}
